/**
 * Raw data-layer helpers — thin wrappers around `supabase.from` / `supabase.rpc`.
 *
 * These exist so that components and pages don't import the supabase client
 * directly for ad-hoc reads/writes. Each helper preserves the original
 * `{ data, error }` shape so call sites stay minimal.
 *
 * If a query grows beyond a one-liner or needs caching, move it into a
 * proper React Query hook under `src/data/queries/` or `src/data/mutations/`.
 */
import { supabase } from "@/integrations/supabase/client";

// ─── burgers ────────────────────────────────────────────────────────
export const BURGER_DETAIL_COLUMNS =
  "id, name, slug, producer_id, canonical_image_url, created_by, description, canonical_patty_type, canonical_cooking_level, canonical_is_smashed, canonical_patty_amount, canonical_toppings, canonical_cheese, canonical_sauces, canonical_bun_type";

export const getBurgerById = (id: string) =>
  supabase.from("burgers").select(BURGER_DETAIL_COLUMNS).eq("id", id).maybeSingle();

export const getBurgerBySlug = (slug: string) =>
  supabase.from("burgers").select(BURGER_DETAIL_COLUMNS).eq("slug", slug).maybeSingle();

export const refreshBurgerById = (id: string) =>
  supabase
    .from("burgers")
    .select(
      "id, name, producer_id, canonical_image_url, created_by, description, canonical_patty_type, canonical_cooking_level, canonical_is_smashed, canonical_patty_amount, canonical_toppings, canonical_cheese, canonical_sauces, canonical_bun_type",
    )
    .eq("id", id)
    .maybeSingle();

export const countBurgersForProducer = (producerId: string) =>
  supabase.from("burgers").select("id", { count: "exact", head: true }).eq("producer_id", producerId);

export const listBurgersByProducer = (producerId: string) =>
  supabase
    .from("burgers")
    .select("id, slug, name, canonical_image_url")
    .eq("producer_id", producerId)
    .order("name");

export const listSiblingBurgers = (producerId: string, excludeBurgerId: string) =>
  supabase
    .from("burgers")
    .select("id, slug, name, canonical_image_url")
    .eq("producer_id", producerId)
    .neq("id", excludeBurgerId)
    .limit(5);

export const listBurgersByIds = (ids: string[]) =>
  supabase.from("burgers").select("id, slug, name, producer_id, canonical_image_url").in("id", ids);

export const listBurgersForProducerNames = (producerId: string) =>
  supabase.from("burgers").select("id, name").eq("producer_id", producerId);

export const searchBurgersByName = (q: string) =>
  supabase
    .from("burgers")
    .select("id, slug, name, producer_id, canonical_image_url")
    .ilike("name", `%${q}%`)
    .limit(5);

export function searchBurgersForAdd(q: string, producerId: string | null) {
  let query = supabase
    .from("burgers")
    .select("id,name,producer_id,producers(name)")
    .ilike("name", `%${q}%`)
    .limit(50);
  if (producerId) query = query.eq("producer_id", producerId);
  return query;
}

export const getBurgerByProducerName = (producerId: string, name: string) =>
  supabase.from("burgers").select("id, name").eq("producer_id", producerId).ilike("name", name).maybeSingle();

export const getBurgerBySlugMaybe = (slug: string) =>
  supabase.from("burgers").select("id, slug").eq("slug", slug).maybeSingle();

export const getBurgerForOwner = (id: string) =>
  supabase.from("burgers").select("id, producer_id").eq("id", id).maybeSingle();

export const getBurgerEmailMeta = (id: string) =>
  supabase.from("burgers").select("created_by, name, slug").eq("id", id).maybeSingle();

export const insertBurger = (row: {
  name: string;
  slug: string;
  producer_id: string;
  created_by?: string;
}) => supabase.from("burgers").insert(row).select("id").single();

// ─── producers ──────────────────────────────────────────────────────
const PRODUCER_DETAIL_COLUMNS =
  "id, name, slug, logo_url, website_url, instagram_url, description, created_by";

export const getProducerById = (id: string) =>
  supabase.from("producers").select(PRODUCER_DETAIL_COLUMNS).eq("id", id).maybeSingle();

export const getProducerBySlug = (slug: string) =>
  supabase.from("producers").select(PRODUCER_DETAIL_COLUMNS).eq("slug", slug).maybeSingle();

export const getProducerNameSlug = (id: string) =>
  supabase.from("producers").select("id, name, slug").eq("id", id).maybeSingle();

export const getProducerIdSlug = (id: string) =>
  supabase.from("producers").select("id, slug").eq("id", id).maybeSingle();

export const getProducerIdSlugByBaseSlug = (slug: string) =>
  supabase.from("producers").select("id,slug").eq("slug", slug).limit(1).maybeSingle();

export const listProducersByIds = (ids: string[]) =>
  supabase.from("producers").select("id, name").in("id", ids);

export const listProducersForFeed = (ids: string[]) =>
  supabase.from("producers").select("id, slug, name").in("id", ids);

export const searchProducersByName = (q: string) =>
  supabase.from("producers").select("id, slug, name, logo_url").ilike("name", `%${q}%`).limit(5);

export const searchProducersForAdd = (q: string) =>
  supabase.from("producers").select("id,name").ilike("name", `%${q}%`).limit(8);

export const countProducersCreatedBy = (userId: string) =>
  supabase.from("producers").select("id", { count: "exact", head: true }).eq("created_by", userId);

export const insertProducer = (row: { name: string; slug: string; created_by?: string }) =>
  supabase.from("producers").insert(row).select("id, slug").single();

// ─── checkins ───────────────────────────────────────────────────────
const FEED_CHECKIN_COLUMNS =
  "id, user_id, burger_id, rating, place_name, place_id, place_address, lat, lng, photo_url, created_at";

export const listOwnRecentCheckins = (userId: string, limit = 3) =>
  supabase
    .from("checkins")
    .select(FEED_CHECKIN_COLUMNS)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

export const listCheckinsByUsers = (
  userIds: string[],
  range: { from: number; to: number },
) =>
  supabase
    .from("checkins")
    .select(FEED_CHECKIN_COLUMNS)
    .in("user_id", userIds)
    .order("created_at", { ascending: false })
    .range(range.from, range.to);

export const countCheckinsForUser = (userId: string) =>
  supabase.from("checkins").select("id", { count: "exact", head: true }).eq("user_id", userId);

export const listCheckinBurgerIds = (userId: string) =>
  supabase.from("checkins").select("burger_id").eq("user_id", userId);

export const listTopBurgerCheckinsForUser = (userId: string) =>
  supabase
    .from("checkins")
    .select("id, burger_id, rating, photo_url")
    .eq("user_id", userId)
    .gte("rating", 4)
    .order("rating", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(6);

export const listJournalCheckinsForUser = (userId: string, range: { from: number; to: number }) =>
  supabase
    .from("checkins")
    .select(`
      id,
      burger_id,
      rating,
      created_at,
      photo_url,
      notes,
      place_name,
      place_address,
      place_id,
      patty_type,
      patty_amount,
      cooking_level,
      is_smashed,
      cheese,
      sauces,
      toppings,
      bun_type,
      burgers (
        name,
        slug,
        canonical_patty_type,
        canonical_patty_amount,
        canonical_cooking_level,
        canonical_is_smashed,
        canonical_cheese,
        canonical_sauces,
        canonical_toppings,
        canonical_bun_type,
        producers (
          id,
          name,
          slug
        )
      )
    `)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .range(range.from, range.to);

export const listProducerCheckinLocations = (burgerIds: string[]) =>
  supabase
    .from("checkins")
    .select("place_name, place_address, place_id, created_at, user_id")
    .in("burger_id", burgerIds)
    .not("place_name", "is", null)
    .order("created_at", { ascending: false })
    .limit(50);

// ─── profiles ───────────────────────────────────────────────────────
// Returns the owner's profile + private notification/marketing preferences.
// Sensitive preference columns are not directly SELECTable via the Data API
// (column-level GRANTs revoked); they're fetched via `get_my_notification_preferences`
// RPC and merged into the result so callers see the same shape as before.
type MyNotificationPreferences = {
  email_follower_notifications: boolean | null;
  email_checkin_notifications: boolean | null;
  email_proposal_notifications: boolean | null;
  push_follower_notifications: boolean | null;
  push_like_notifications: boolean | null;
  push_proposal_notifications: boolean | null;
  marketing_emails_opt_in: boolean | null;
};

export const getMyProfileSettings = async (userId: string) => {
  const [base, prefs] = await Promise.all([
    supabase
      .from("profiles")
      .select(
        "display_name, is_public, show_checkins, show_stats, show_favorites, avatar_url, header_url, username, instagram_url, tiktok_url, linkedin_url, bio",
      )
      .eq("id", userId)
      .maybeSingle(),
    supabase.rpc("get_my_notification_preferences"),
  ]);
  if (base.error) return base;
  const rows = (prefs.data ?? []) as MyNotificationPreferences[];
  const prefsRow = Array.isArray(rows) ? rows[0] ?? null : (rows as unknown as MyNotificationPreferences);
  return {
    ...base,
    data: base.data
      ? ({ ...base.data, ...(prefsRow ?? {}) } as typeof base.data & Partial<MyNotificationPreferences>)
      : base.data,
  };
};



export const insertEmptyProfile = (id: string, displayName: string | null) =>
  supabase.from("profiles").insert({ id, display_name: displayName });

export const upsertProfileMarketingOptIn = (id: string, optIn: boolean) =>
  supabase
    .from("profiles")
    .upsert({ id, marketing_emails_opt_in: optIn }, { onConflict: "id" });

export const getProfileBasicHeader = (userId: string) =>
  supabase.from("profiles").select("display_name, avatar_url").eq("id", userId).maybeSingle();

export const getProfileDisplayName = (userId: string) =>
  supabase.from("profiles").select("display_name").eq("id", userId).single();

export const getProfileForCheckinEmail = (userId: string) =>
  supabase.from("profiles").select("display_name").eq("id", userId).maybeSingle();

export const getProfileForProposerName = (userId: string) =>
  supabase.from("profiles").select("display_name, username").eq("id", userId).single();

export const getProfilePublicForPrompt = (userId: string) =>
  supabase
    .from("profiles")
    .select("display_name, avatar_url, username, bio")
    .eq("id", userId)
    .maybeSingle();

export const getProfilePublicDisplayName = (userId: string) =>
  supabase.from("profiles_public").select("display_name").eq("id", userId).maybeSingle();

export const findUsernameOwner = (username: string) =>
  supabase.from("profiles_public").select("id").ilike("username", username).single();

export const listProfilesPublicByIds = (ids: string[]) =>
  supabase.from("profiles_public").select("id, display_name, avatar_url").in("id", ids);

export const listProfilesPublicForFeed = (ids: string[]) =>
  supabase.from("profiles_public").select("id, display_name, username").in("id", ids);

export function listNewProfiles(excludeIds: string[], sinceIso: string, currentUserId: string) {
  let query = supabase
    .from("profiles_public")
    .select("id, display_name, avatar_url, username, created_at")
    .gte("created_at", sinceIso)
    .not("id", "eq", currentUserId)
    .not("id", "in", "(d28f94be-4b3c-42da-ae3f-ead9e1e197a1)")
    .order("created_at", { ascending: false })
    .limit(10);
  if (excludeIds.length > 0) {
    query = query.not("id", "in", `(${excludeIds.join(",")})`);
  }
  return query;
}

export const searchProfilesPublicByName = (q: string) =>
  supabase
    .from("profiles_public")
    .select("id, display_name, avatar_url, is_public, username")
    .or(`display_name.ilike.%${q}%,username.ilike.%${q}%`)
    .not("id", "in", "(d28f94be-4b3c-42da-ae3f-ead9e1e197a1)")
    .limit(5);

export const searchFriendCandidates = (q: string, currentUserId: string) =>
  supabase
    .from("profiles_public")
    .select("id, display_name, avatar_url, username")
    .not("id", "eq", currentUserId)
    .or(`display_name.ilike.%${q}%,username.ilike.%${q}%`)
    .limit(20);

// ─── proposals ──────────────────────────────────────────────────────
export const listPendingProposals = (entityType: string, entityId: string) =>
  supabase
    .from("description_proposals")
    .select("id, proposed_description, proposed_by, created_at, field_type")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });

export const getMyPendingProposal = (
  entityType: string,
  entityId: string,
  userId: string,
  fieldType: string,
) =>
  supabase
    .from("description_proposals")
    .select("id, proposed_description, created_at")
    .eq("entity_type", entityType)
    .eq("entity_id", entityId)
    .eq("proposed_by", userId)
    .eq("field_type", fieldType)
    .eq("status", "pending")
    .maybeSingle();

// ─── likes ──────────────────────────────────────────────────────────
export const getCheckinLikeCount = (checkinId: string) =>
  supabase.rpc("get_checkin_like_count" as any, { p_checkin_id: checkinId });

// Batch summary: like_count + viewer_liked for many checkins in a single call.
// Powers the feed-wide CheckinLikesProvider so a 20-card feed makes 1 request
// instead of 40 (1 count + 1 viewer-liked per card).
export const getCheckinLikesSummary = (checkinIds: string[]) =>
  supabase.rpc("get_checkin_likes_summary" as any, { p_checkin_ids: checkinIds });

export const getUserCheckinLike = (checkinId: string, userId: string) =>
  supabase
    .from("checkin_likes" as any)
    .select("id")
    .eq("checkin_id", checkinId)
    .eq("user_id", userId)
    .maybeSingle();

export const insertCheckinLike = (checkinId: string, userId: string) =>
  supabase.from("checkin_likes" as any).insert({ checkin_id: checkinId, user_id: userId });

export const deleteCheckinLike = (checkinId: string, userId: string) =>
  supabase
    .from("checkin_likes" as any)
    .delete()
    .eq("checkin_id", checkinId)
    .eq("user_id", userId);

// ─── badges ─────────────────────────────────────────────────────────
export const countUserBadges = (userId: string) =>
  (supabase as any)
    .from("user_badges" as any)
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId);

export const listUserBadgesWithSlug = (userId: string) =>
  (supabase as any)
    .from("user_badges")
    .select("id, badges:badge_id(slug)")
    .eq("user_id", userId);

export const listUserBadgesEvidenceForCheckins = (checkinIds: string[]) =>
  (supabase as any)
    .from("user_badges")
    .select("evidence, badges:badge_id(name, slug, icon_name)")
    .in("evidence->>checkin_id", checkinIds);

export const awardInfluencerBadge = (badgeSlug: string, userId: string) =>
  supabase.rpc("award_influencer_badge", { p_badge_slug: badgeSlug, p_user_id: userId });

export const awardBurgerScoutBadges = (userId: string) =>
  supabase.rpc("award_burger_scout_badges", { p_user_id: userId });

export const awardCheckinBadges = (checkinId: string) =>
  (supabase as any).rpc("award_checkin_badges", { p_checkin_id: checkinId });

// ─── stats / RPCs ───────────────────────────────────────────────────
export const getFollowersCountRpc = (userId: string) =>
  supabase.rpc("get_followers_count", { target_user_id: userId });

export const getPublicBurgerStats = () => supabase.rpc("get_public_burger_stats");
export const getPublicProducerStats = () => supabase.rpc("get_public_producer_stats");
export const getPublicBurgerDetails = (burgerId: string) =>
  supabase.rpc("get_public_burger_details", { p_burger_id: burgerId });
export const getPublicBurgerLocations = (burgerId: string) =>
  supabase.rpc("get_public_burger_locations", { p_burger_id: burgerId });
export const getPublicCheckinLocations = (burgerIds: string[]) =>
  supabase.rpc("get_public_checkin_locations", { p_burger_ids: burgerIds });
export const getPublicCheckinsAnonymized = () => supabase.rpc("get_public_checkins_anonymized");

export const getBurgerStatsByIds = (burgerIds: string[]) =>
  supabase.from("burger_stats").select("burger_id, checkins_count").in("burger_id", burgerIds as any);

// ─── locations ──────────────────────────────────────────────────────
export const getLocationStatsRpc = () => supabase.rpc("get_location_stats");

// ─── push tokens ────────────────────────────────────────────────────
export const upsertPushToken = (row: {
  user_id: string;
  token: string;
  platform: string;
  updated_at: string;
}) => supabase.from("push_tokens").upsert(row, { onConflict: "user_id,token" });

export const deletePushToken = (userId: string, token: string) =>
  supabase.from("push_tokens").delete().eq("user_id", userId).eq("token", token);

// ─── follows ────────────────────────────────────────────────────────
export const listFollowingIds = (followerId: string) =>
  supabase.from("follows").select("following_id").eq("follower_id", followerId);

export const insertFollow = (followerId: string, followingId: string) =>
  supabase.from("follows").insert({ follower_id: followerId, following_id: followingId });

export const deleteFollow = (followerId: string, followingId: string) =>
  supabase.from("follows").delete().eq("follower_id", followerId).eq("following_id", followingId);

export const getFollowRelation = (followerId: string, followingId: string) =>
  supabase
    .from("follows")
    .select("id")
    .eq("follower_id", followerId)
    .eq("following_id", followingId)
    .maybeSingle();

// ─── notifications ──────────────────────────────────────────────────
export const listNotificationsForUser = (userId: string, limit = 50) =>
  supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

export const markNotificationRead = (id: string, userId: string) =>
  supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", id)
    .eq("user_id", userId);

export const markAllNotificationsRead = (userId: string) =>
  supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("user_id", userId)
    .is("read_at", null);

export const deleteNotification = (id: string, userId: string) =>
  supabase.from("notifications").delete().eq("id", id).eq("user_id", userId);

// ─── seed admin ─────────────────────────────────────────────────────
export const getProducersBySlugs = (slugs: string[]) =>
  supabase
    .from("producers")
    .select("id,name,slug,logo_url,instagram_url")
    .in("slug", slugs);

// Admin-only. Uses a SECURITY DEFINER RPC to bypass the checkin_locations RLS
// policy (which otherwise hides seed accounts' check-ins from admins).
export const getCityLocationCheckinCounts = () =>
  supabase.rpc("get_seed_city_checkin_counts");

export const getLocationCoords = () =>
  supabase.from("locations").select("id, lat, lng");
